import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  EmploymentStatusType,
  PayType,
} from 'src/app/services/dropdowns/dropdowns.model';

import { FinalLoanApplication } from 'src/app/services/loan/loan.model';
import { LoanService } from 'src/app/services/loan/loan.service';

/**
 * The parent {FormGroup} must contain the following controls.
 *
 * Employer Information {primaryControls}:
 * * EmployerName
 * * Position
 * * Years
 * * Months
 * * Address
 * * AptNumber
 * * POBox
 * * City
 * * State
 * * ZipCode
 * * PhoneNumber
 * * PhoneNumberType
 * * EmailAddress
 * * PayTypeCD
 * * PayPeriod
 * * TakeHomePay
 * * AnnualSalary
 * * NetIncomePPP
 * * HoursPerWeek
 * * HourlyRate
 *
 * Additional Income {secondaryControls}:
 * * OtherIncomes
 * * MonthlyAdditionalIncome
 *
 * The parent {FormGroup} can disable unneeded controls. Disabling will hide the controls from the UI.
 *
 * ```typescript
 * this.formBuilder.group({
 *    POBox: [{ value: '', disabled: true }]
 * });
 * ```
 */
@Component({
  selector: 'app-employer-group',
  templateUrl: './employer-group.component.html',
  styleUrls: ['./employer-group.component.scss'],
})
export class EmployerGroupComponent implements OnInit, OnDestroy {
  @Input() primaryControls: {
    [key: string]: AbstractControl;
  } = {};
  @Input() secondaryControls: {
    [key: string]: AbstractControl;
  } = {};

  /**
   * Used to save the user's progress on the form when there's a pay type change.
   */
  @Input() primaryFormGroup?: FormGroup;
  /**
   * Used to save the user's progress on the form when there's a pay type change.
   */
  @Input() secondaryFormGroup?: FormGroup;

  loanApplication: FinalLoanApplication | null = null;
  coApplicant = false;
  unemployed = false;

  private subscriptions: Array<Subscription> = [];

  constructor(private loanService: LoanService, private router: Router) {
    this.coApplicant = this.router.url.includes('co-applicant');
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.loanService.finalLoanApplication.subscribe(
        (data: FinalLoanApplication | null) => {
          if (data) {
            this.loanApplication = { ...data };

            const employmentKey = this.coApplicant
              ? 'CoApplicantEmployment'
              : 'ApplicantEmployment';
            const additionIncomeKey = this.coApplicant
              ? 'CoApplicantAdditionalIncome'
              : 'ApplicantAdditionalIncome';

            if (data[employmentKey]) {
              // Check if applicant is unemployed or retired.
              this.unemployed =
                data[employmentKey]?.EmploymentStatus ===
                  EmploymentStatusType.unemployed ||
                data[employmentKey]?.EmploymentStatus ===
                  EmploymentStatusType.retired;

              if (this.unemployed) {
                // Disable form controls if the applicant is unemployed or retired.
                this.setFormControls(
                  this.unemployed,
                  data[employmentKey]?.EmploymentStatus ===
                    EmploymentStatusType.retired
                );

                this.primaryControls['TakeHomePay'].setValue(
                  data[employmentKey]?.TakeHomePay || ''
                );
              } else {
                const payType = data[employmentKey]?.PayTypeCD ?? '';

                this.setFormControls();
                this.setFormControlsForPayType(payType);
                this.setFormValues(data[employmentKey], this.primaryControls);
              }
            }

            // Hide the other income controls if the applicant is retired.
            if (
              data[employmentKey]?.EmploymentStatus ===
              EmploymentStatusType.retired
            ) {
              this.onClearOtherIncomesClick();
              this.secondaryControls['OtherIncomes'].disable();
              this.secondaryControls['MonthlyAdditionalIncome'].disable();
            } else {
              if (data[additionIncomeKey]) {
                this.setFormValues(
                  data[additionIncomeKey],
                  this.secondaryControls
                );
                this.onOtherIncomeChange(
                  data[additionIncomeKey]?.OtherIncomes ?? []
                );
              }
            }
          }
        }
      )
    );
  }

  ngOnDestroy(): void {
    if (this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }

  getFormControl(
    key: string,
    controls: {
      [key: string]: AbstractControl;
    }
  ): FormControl {
    return controls[key] as FormControl;
  }

  setFormValues(
    data: any,
    controls: {
      [key: string]: AbstractControl;
    }
  ) {
    // Set form values
    Object.keys(data).forEach((key) => {
      // Check if key exists on current form.
      if (this.getFormControl(key, controls)) {
        this.getFormControl(key, controls).setValue(data?.[key]);
      }
    });
  }

  /**
   * Disable {FormControl}s on {primaryControls} if applicant is unemployed or retired.
   *
   * @param disable Defaults to `false`. Will disable the control if `true` otherwise it will be enabled.
   * @param isRetired Defaults to `false`. If retired, form will keep the control `TakeHomePay` enabled.
   */
  setFormControls(disable = false, isRetired = false) {
    // Do NOT do anything to these form controls, they are already deactivated and removed from the UI.
    const deactivatedControlKeys = [
      'AptNumber',
      'POBox',
      'PhoneNumberType',
      'EmailAddress',
      'NetIncomePPP',
    ];

    Object.keys(this.primaryControls).forEach((key) => {
      // Check if key exists on controls.
      if (!deactivatedControlKeys.includes(key)) {
        if (disable) {
          if ((isRetired && key !== 'TakeHomePay') || !isRetired) {
            this.getFormControl(key, this.primaryControls).disable();
          }
        } else {
          this.getFormControl(key, this.primaryControls).enable();
        }
      }
    });
  }

  /**
   * Disable {FormControl}s on {primaryControls} based on applicant's pay type.
   *
   * @param payType Defaults to empty string.
   */
  setFormControlsForPayType(payType = '') {
    const disableKeysForHourly = ['AnnualSalary'];
    const disableKeysForOther = ['HoursPerWeek', 'HourlyRate'];

    Object.keys(this.primaryControls).forEach((key) => {
      if (payType === PayType.hourly) {
        if (disableKeysForHourly.includes(key)) {
          this.getFormControl(key, this.primaryControls).disable();
        } else if (disableKeysForOther.includes(key)) {
          this.getFormControl(key, this.primaryControls).enable();
        }
      } else {
        if (disableKeysForHourly.includes(key)) {
          this.getFormControl(key, this.primaryControls).enable();
        } else if (disableKeysForOther.includes(key)) {
          this.getFormControl(key, this.primaryControls).disable();
        }
      }
    });
  }

  onPayTypeChange(value: string) {
    // Get form data and update the loan object to save progress.
    if (this.primaryFormGroup && this.secondaryFormGroup) {
      this.loanService.setApplicantEmployer(
        {
          ...this.primaryFormGroup.value,
          ...this.secondaryFormGroup.value,
        },
        this.coApplicant
      );
    }

    this.loanService.clearOnPayTypeChange(value, this.coApplicant);

    this.setFormControlsForPayType(value);
  }

  onClearOtherIncomesClick() {
    const otherIncomes = this.getFormControl(
      'OtherIncomes',
      this.secondaryControls
    );
    const monthlyAdditionalIncome = this.getFormControl(
      'MonthlyAdditionalIncome',
      this.secondaryControls
    );

    otherIncomes.setValue([]);
    monthlyAdditionalIncome.setValue('');
    monthlyAdditionalIncome.clearValidators();
    monthlyAdditionalIncome.updateValueAndValidity();
  }

  onOtherIncomeChange(value: Array<string>) {
    const control = this.getFormControl(
      'MonthlyAdditionalIncome',
      this.secondaryControls
    );

    if (value.length === 0) {
      control.clearValidators();
    } else {
      control.addValidators([Validators.required, Validators.min(0)]);
    }

    control.updateValueAndValidity();
  }
}
